import React from 'react';

const accent = {
  elite: 'text-green-600 dark:text-green-400',
  mid: 'text-brand-600 dark:text-brand-400',
  low: 'text-amber-600 dark:text-amber-400',
  runner: 'text-blue-600 dark:text-blue-400',
  contender: 'text-gray-500 dark:text-gray-400',
};

const leagueColor = {
  Elite: accent.elite,
  Challenger: accent.low,
  'Runner-Up': accent.runner,
  Contender: accent.contender,
};

const fmt = (n, digits = 2) => (n == null ? '—' : Number(n).toFixed(digits));
const upper = (s) => (s ? String(s).toUpperCase() : '');
const verb = (tense, present, past) => (tense === 'present' ? present : past);
const eyebrowOf = (monthLabel, year, user) => {
  const prefix = `${upper(monthLabel)} ${year}`.trim();
  const userUpper = upper(user || '');
  if (!userUpper) return prefix;
  return (
    <>
      {prefix}
      {prefix ? ' · ' : ''}
      <span className="font-medium text-gray-700 dark:text-gray-300">{userUpper}</span>
    </>
  );
};

/**
 * Build the eyebrow + headline (JSX) + lede (string) for the Rankings detail hero.
 */
export function rankingHero({
  user,
  monthLabel,
  year,
  rank,
  total,
  streak,
  submitted,
  daysInPeriod,
  tense = 'present',
  periodPhrase = 'this month',
}) {
  const eyebrow = eyebrowOf(monthLabel, year, user);

  if (!total) {
    return {
      eyebrow,
      headline: <>No ranking data {periodPhrase}.</>,
      lede: '',
    };
  }

  // Hero displays the user's leaderboard position — an integer ordinal,
  // not a continuous mean — so render without decimals.
  const rankNum = rank != null ? String(Math.trunc(Number(rank))) : '—';
  const headline = (
    <>
      {verb(tense, 'Your field win rate places you', 'Your field win rate placed you')}{' '}
      <span className={accent.mid}>#{rankNum}</span> {periodPhrase}.
    </>
  );

  const ledeBits = [];
  if (submitted != null && daysInPeriod != null) {
    ledeBits.push(
      `${verb(tense, 'You have submitted', 'You submitted')} ${submitted}/${daysInPeriod} days`,
    );
  }
  if (streak && streak > 0) {
    ledeBits.push(
      `${verb(tense, 'are running', 'ran')} a ${streak}-day top-10 streak`,
    );
  }
  const ledeText = ledeBits.length ? `${ledeBits.join(' and ')}.` : '';

  // When the user missed any days, clarify the scope of the metrics: each
  // metric below is computed only over the days they submitted, compared
  // against peers' submissions on those same days.
  const hasMissedDays =
    submitted != null && daysInPeriod != null && submitted < daysInPeriod;
  if (!hasMissedDays) {
    return { eyebrow, headline, lede: ledeText };
  }

  return {
    eyebrow,
    headline,
    lede: (
      <>
        {ledeText}
        {ledeText ? ' ' : null}
        <span className="text-amber-600 dark:text-amber-400">
          Metrics cover only your {submitted} submitted{' '}
          {submitted === 1 ? 'day' : 'days'}, judged against peers on those same days.
        </span>
      </>
    ),
  };
}

// League boundaries in error space: lower error = better, so "promote" is the
// stricter threshold above (the cutoff to climb into) and "demote" is the looser
// threshold the user must stay below to keep their current league.
function leagueBoundaries(league, thresholds) {
  const t = thresholds || {};
  if (league === 'Elite') {
    return { promote: null, demote: { name: 'Challenger', value: t.elite } };
  }
  if (league === 'Challenger') {
    return {
      promote: { name: 'Elite', value: t.elite },
      demote: { name: 'Runner-Up', value: t.challenger },
    };
  }
  if (league === 'Runner-Up') {
    return {
      promote: { name: 'Challenger', value: t.challenger },
      demote: { name: 'Contender', value: t.runner_up },
    };
  }
  return { promote: { name: 'Runner-Up', value: t.runner_up }, demote: null };
}

// Qualitative read on the demotion margin so a reader does not have to interpret
// raw % alone. Returns null when there is no positive margin to qualify (caller
// routes to at-risk copy instead). Cutoffs picked from typical day-over-day MWI
// variance: < 5% is rounding-distance from the boundary; 25%+ survives a slump.
function safetyTag(marginPct) {
  if (marginPct == null || marginPct <= 0) return null;
  if (marginPct >= 25) return 'safe';
  if (marginPct >= 10) return 'comfortable';
  if (marginPct >= 5) return 'narrow';
  return 'thin';
}

// Headline tail: safety verdict against the demote tier. Returns null when there
// is no demote tier (Contender) or no comparable score.
function marginClause({ marginPct, demote }) {
  if (!demote || demote.value == null || marginPct == null) return null;
  const tierClass = leagueColor[demote.name] || accent.mid;
  if (marginPct <= 0) {
    return (
      <>
        at risk of slipping to <span className={tierClass}>{demote.name}</span>
      </>
    );
  }
  const tag = safetyTag(marginPct);
  return (
    <>
      a <span className={accent.mid}>{tag}</span>{' '}
      <span className={accent.mid}>{marginPct}%</span> margin to{' '}
      <span className={tierClass}>{demote.name}</span>
    </>
  );
}

// Lede sentence (or Contender-headline fragment): the climb to the next league.
// Emits no trailing punctuation — the caller decides whether it is a sentence
// or a tail fragment.
function climbClause({ climbPct, promote, tense }) {
  if (!promote || promote.value == null || climbPct == null) return null;
  const tierClass = leagueColor[promote.name] || accent.mid;
  if (climbPct > 0) {
    return (
      <>
        <span className={tierClass}>{promote.name}</span>{' '}
        {verb(tense, 'is', 'was')}{' '}
        <span className={accent.mid}>{climbPct}%</span> away
      </>
    );
  }
  if (climbPct === 0) {
    return (
      <>
        Right at the <span className={tierClass}>{promote.name}</span> threshold
      </>
    );
  }
  return (
    <>
      Already {verb(tense, 'meeting', 'met')} the{' '}
      <span className={tierClass}>{promote.name}</span> threshold
    </>
  );
}

/**
 * Build the eyebrow + headline (JSX) + lede (JSX) for the Errors detail hero.
 * Mirrors the Rankings hero shape: one short bold headline, one supporting line.
 *
 * Two ideas, two sentences:
 *   - Headline: current league + qualitative safety verdict against the demote
 *     tier (or, for Contender, the climb fragment since there is no margin).
 *   - Lede: climb to the next league + the score reference (or, for Elite and
 *     Contender, just the score reference).
 *
 * Stays narrative + qualitative on purpose — `LeagueTimelineCard` directly
 * below already plots the absolute MWI gap and cushion, so the hero need not
 * restate raw numbers.
 */
// Compact heads-up for the missed-day penalty: if the forecaster skipped at
// least one submission, the monthly avg score uses a peer-Q75 fill on those
// days. Returns null when there is nothing to say so AnalyticsHero can skip
// rendering the footnote slot entirely.
function missedDaysFootnote({ missedDays, periodPhrase }) {
  if (missedDays == null || missedDays <= 0) return null;
  const noun = missedDays === 1 ? 'day' : 'days';
  return (
    <>
      <span className="font-medium text-gray-700 dark:text-gray-200">
        {missedDays} {noun} missed
      </span>{' '}
      {periodPhrase}, peer-Q75 fill applied to your effective score.
    </>
  );
}

export function errorsHero({
  user,
  monthLabel,
  year,
  avgScore,
  league,
  leagueId,
  submissions,
  missedDays,
  maxMissedDays,
  thresholds,
  metricLabel,
  tense = 'present',
  periodPhrase = 'this month',
}) {
  const eyebrow = eyebrowOf(monthLabel, year, user);
  const scoreStr = fmt(avgScore);
  const dayN = submissions ?? 0;
  const dayLabel = `${dayN} ${dayN === 1 ? 'day' : 'days'}`;
  const scoreWithMetric = metricLabel ? `${scoreStr} ${metricLabel}` : scoreStr;
  const leagueClass = league ? leagueColor[league] || accent.mid : '';
  const footnote = missedDaysFootnote({ missedDays, periodPhrase });

  // Disqualified branch — short-circuit before any league-relative analysis.
  // A forecaster who crossed MAX_MISSED_DAYS has no league standing to
  // compare against and no climb path; the hero must say so plainly (the
  // LeagueIneligibilityBanner above and the LeagueTimelineCard below
  // already do — this aligns the third surface).
  if (leagueId === 'unqualified') {
    const missedN = missedDays ?? 0;
    const missedNoun = missedN === 1 ? 'day' : 'days';
    const allowance = maxMissedDays ?? 5;
    const overLimit = Math.max(0, missedN - allowance);
    const overNoun = overLimit === 1 ? 'day' : 'days';

    return {
      eyebrow,
      headline: (
        <>
          <span className={accent.low}>Not evaluated for league rewards</span>{' '}
          {periodPhrase}.
        </>
      ),
      lede: (
        <>
          <span className={`font-medium ${accent.low}`}>
            {missedN} missed {missedNoun}
          </span>
          {overLimit > 0 ? (
            <>
              {' '}— {overLimit} {overNoun} over the {allowance}-day allowance
            </>
          ) : null}
          .{' '}
          {verb(tense, 'Averaging', 'Averaged')}{' '}
          <span className={accent.mid}>{scoreWithMetric}</span> across{' '}
          {dayLabel} with submissions.
        </>
      ),
      footnote: (
        <>Daily-rank rewards still apply on the days you submitted.</>
      ),
    };
  }

  const { promote, demote } = leagueBoundaries(league, thresholds);
  const climbPct =
    promote && promote.value != null && avgScore != null
      ? Math.round(((avgScore - promote.value) / promote.value) * 100)
      : null;
  const marginPct =
    demote && demote.value != null && avgScore != null
      ? Math.round(((demote.value - avgScore) / demote.value) * 100)
      : null;

  const climb = climbClause({ climbPct, promote, tense });
  const margin = marginClause({ marginPct, demote });

  // No tier guidance available (missing thresholds or score) — anchor on the score itself.
  if (!climb && !margin) {
    return {
      eyebrow,
      headline: (
        <>
          {verb(tense, 'Your average error is', 'Your average error was')}{' '}
          <span className={accent.mid}>{scoreWithMetric}</span> {periodPhrase}.
        </>
      ),
      lede: <>Across {dayLabel} with submissions.</>,
      footnote,
    };
  }

  const verbWord = verb(tense, 'Holding', 'Held');
  const averagingSentence = (
    <>
      {verb(tense, 'Averaging', 'Averaged')}{' '}
      <span className={accent.mid}>{scoreWithMetric}</span> across {dayLabel} with submissions.
    </>
  );

  // Contender — no demote tier to judge safety against, so the climb belongs
  // in the headline and the lede is just the score reference.
  if (!demote) {
    return {
      eyebrow,
      headline: (
        <>
          {verbWord} <span className={leagueClass}>{league}</span> league {periodPhrase}
          {climb ? <>, {climb}</> : null}.
        </>
      ),
      lede: averagingSentence,
      footnote,
    };
  }

  // Elite / Challenger / Runner-Up — safety verdict in the headline; climb
  // (when there is one) precedes the score in the lede as its own sentence.
  return {
    eyebrow,
    headline: (
      <>
        {verbWord} <span className={leagueClass}>{league}</span> league {periodPhrase}
        {margin ? <>, {margin}</> : null}.
      </>
    ),
    lede: climb ? (
      <>
        {climb}. {averagingSentence}
      </>
    ) : (
      averagingSentence
    ),
    footnote,
  };
}
